// src/frontend/src/utils/ApiUtils.js
import { config } from "../Constants";

/**
 * Fetch API wrapper that handles authentication and common error scenarios
 * 
 * @param {string} url - The API endpoint (without the base URL)
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} - The fetch response
 */
export const fetchWithAuth = async (url, options = {}) => {
    // Get the current token
    const token = localStorage.getItem('authToken');

    // Set default headers
    const headers = {
        'Content-Type': 'application/json',
        ...options.headers
    };

    // Add authorization if token exists
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    // Make the request
    const requestUrl = url.startsWith('http') ? url : `${config.url}${url}`;

    try {
        const response = await fetch(requestUrl, {
            ...options,
            headers,
            credentials: 'include'
        });

        // Handle 401 errors
        if (response.status === 401) {
            console.warn(`Authentication error for ${url}`);

            // You could trigger a reauth flow here
            // This is just a basic implementation that could be enhanced

            // Dispatch a custom event that AuthContext can listen for
            const authErrorEvent = new CustomEvent('auth:error', {
                detail: { status: 401, url }
            });
            window.dispatchEvent(authErrorEvent);
        }

        return response;
    } catch (error) {
        console.error(`Network error for ${url}:`, error);
        throw error;
    }
};

/**
 * Refreshes the authentication token
 * 
 * @returns {Promise<boolean>} - True if refresh was successful
 */
export const refreshAuthToken = async () => {
    try {
        const currentToken = localStorage.getItem('authToken');
        if (!currentToken) return false;

        const response = await fetchWithAuth('/api/v1/auth/refresh', {
            method: 'POST'
        });

        if (response.ok) {
            const data = await response.json();
            if (data.result === 'SUCCESS' && data.data) {
                localStorage.setItem('authToken', data.data);
                return true;
            }
        }
        return false;
    } catch (error) {
        console.error('Failed to refresh token:', error);
        return false;
    }
};